import { FormInstance } from "antd";
import { QueryOperation, SearchQuery } from "../code/search-query/search-query";
import {
  buildSearchQuery,
  comboboxValue,
  dateValue,
  QueryFieldMapping,
} from "../code/search-query/query-mapper";
import { DataColumn } from "../controls/Data.default";
import { DynamicSchema } from "../controls/DynamicForm.default";
import { InputObject } from "../controls/default";
import {
  createScopedFilterRequest,
  createSearchRequest,
} from "../hooks/useSearchRequest";
import {
  getCompanies,
  getCompanyAssets,
  getCompanyDepartments,
  getCompanyUserRoles,
} from "../repository/data.repository";

export interface UsersViewForm {
  readonly userName: string;
  readonly firstName: string;
  readonly middleName: string;
  readonly lastName: string;
  readonly dateOfBirth: string;
  readonly email: string;
  readonly emailConfirmed: boolean;
  readonly phoneNumber: string;
  readonly companyPhoneNumber: string;
  readonly company?: InputObject;
  readonly department?: InputObject;
  readonly role?: InputObject;
  readonly asset?: InputObject;
}

export const usersViewColumns: DataColumn[] = [
  {
    title: "Company",
    key: "companyName",
    width: 192,
    sort: true,
    defaultSort: "asc",
    fixed: "left",
  },
  {
    title: "Department",
    key: "departmentName",
    width: 192,
    sort: true,
  },
  // PD-01 §4.3 names role and vessel/site among the All-grid fields. Both were already on the wire and
  // populated — role on 143 of 143 users, asset on 89 — so this is a column that was never asked for.
  //
  // This is the COMPANY role (the job position: Seaman, Master), not the platform role. The grid DTO
  // carries only that one, and it is the sense §4.5 means by "role/job position". It is populated for
  // every row precisely because affiliates — the only users without one — are no longer on this tab.
  {
    title: "Role",
    key: "roleName",
    width: 160,
    sort: true,
  },
  // Labelled "Asset", not "Vessel/Site". The product serves six industries and seven of its sixteen
  // companies are not shipping, so a vessel-shaped label is wrong for them; the reconciled PD-01 carries
  // a terminology note recording that its "vessel/site" and the product's asset are the same thing.
  {
    title: "Asset",
    key: "assetName",
    width: 192,
    sort: true,
  },
  {
    title: "User Name",
    key: "userName",
    width: 192,
    sort: true,
  },
  {
    title: "First Name",
    key: "firstName",
    width: 192,
    sort: true,
  },
  {
    title: "Middle Name",
    key: "middleName",
    width: 192,
    sort: true,
  },
  {
    title: "Last Name",
    key: "lastName",
    width: 192,
    sort: true,
  },
  {
    title: "Date Of Birth",
    key: "dateOfBirth",
    width: 128,
    format: "DD/MM/YYYY",
    sort: true,
  },
  {
    title: "Email",
    key: "email",
    width: 256,
    sort: true,
  },
  {
    title: "Email Confirmed",
    key: "emailConfirmed",
    width: 128,
    sort: true,
  },
  {
    title: "Phone",
    key: "phoneNumber",
    width: 192,
    sort: true,
  },
  {
    title: "Company Phone",
    key: "companyPhoneNumber",
    width: 192,
    sort: true,
  },
  // §4.3 lists "last login" among the All-grid fields. A real column rather than a derived
  // MAX(LoginHistory.Timestamp), so sorting on it stays cheap. Blank until the person signs in again:
  // the field was renamed from a dead LastOnline and deliberately NOT back-filled, because 902 of the
  // 2 086 seeded login-history rows are dated in the FUTURE and a back-fill would import that.
  {
    title: "Last Login",
    key: "lastLoginAt",
    width: 144,
    format: "DD/MM/YYYY HH:mm",
    sort: true,
  },
  // Admin requirement 2026-11-03 — Account Status + Activation Date on every
  // user management grid.
  {
    title: "Status",
    key: "isActive",
    width: 110,
    sort: true,
    transform: (v: boolean) => (v === false ? "Inactive" : "Active"),
  },
  {
    title: "Activated On",
    key: "activatedAt",
    width: 148,
    format: "DD/MM/YYYY",
    sort: true,
  },
  {
    title: "Deactivated On",
    key: "deactivatedAt",
    width: 148,
    format: "DD/MM/YYYY",
    sort: true,
  },
];

const usersQueryMappings: QueryFieldMapping[] = [
  { formField: "company", queryField: "companyUid", operation: QueryOperation.Equal, transform: comboboxValue },
  // §4.7.1 requires users to be filterable by role, company, department and vessel/site. Company was the
  // only one built. These three filter on the UID rather than the displayed name, so two departments that
  // happen to share a name across companies stay distinct.
  { formField: "department", queryField: "departmentUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "role", queryField: "roleUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "asset", queryField: "assetUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "userName" },
  { formField: "firstName" },
  { formField: "middleName" },
  { formField: "lastName" },
  { formField: "dateOfBirth", operation: QueryOperation.Equal, transform: dateValue },
  { formField: "email" },
  { formField: "emailConfirmed", operation: QueryOperation.Equal },
  { formField: "phoneNumber" },
  { formField: "companyPhoneNumber" },
];

export const mapUsersViewQuery = (values: UsersViewForm): SearchQuery =>
  buildSearchQuery(values, usersQueryMappings);

// A factory rather than a constant, because Department / Role / Asset read the Company field to scope their
// own options. `dependsOn: ["company"]` is a cascade RESET, not a visibility rule: the three stay usable
// with no company chosen — where they list every company's departments, roles and assets, which is what
// makes "show me everyone in a Vessel department, whoever they work for" possible — and a stale selection
// is cleared the moment the company changes, so a department from company A cannot survive a switch to B.
export const usersSearchSchema = (form: FormInstance<any>): DynamicSchema => ({
  groups: [
    {
      controls: [
        {
          type: "combobox",
          name: "company",
          label: "Company",
          request: createSearchRequest(getCompanies, "name"),
        },
        {
          type: "combobox",
          name: "department",
          label: "Department",
          dependsOn: ["company"],
          request: createScopedFilterRequest(getCompanyDepartments, "name", () => [
            ["companyUid", form.getFieldValue("company")?.value],
          ], undefined, { subDisplayField: "companyName" }),
        },
        {
          type: "combobox",
          name: "role",
          label: "Role",
          dependsOn: ["company"],
          // `subDisplayField` carries the company under each option, and it is not decoration: role and
          // department names repeat across companies (298 roles over 16 companies), so with no company
          // chosen the list shows several identical "Master" entries that select DIFFERENT role records.
          // The walk found exactly that. The secondary line is the house convention for this — the
          // counselling and training dialogs already use it to tell people apart across companies.
          request: createScopedFilterRequest(getCompanyUserRoles, "name", () => [
            ["companyUid", form.getFieldValue("company")?.value],
          ], undefined, { subDisplayField: "companyName" }),
        },
        {
          type: "combobox",
          name: "asset",
          label: "Asset",
          dependsOn: ["company"],
          request: createScopedFilterRequest(getCompanyAssets, "name", () => [
            ["companyUid", form.getFieldValue("company")?.value],
          ], undefined, { subDisplayField: "companyName" }),
        },
        { type: "textbox", name: "userName", label: "User Name" },
        { type: "textbox", name: "firstName", label: "First Name" },
        { type: "textbox", name: "middleName", label: "Middle Name" },
        { type: "textbox", name: "lastName", label: "Last Name" },
        { type: "datepicker", name: "dateOfBirth", label: "Date Of Birth" },
        { type: "textbox", name: "email", label: "Email" },
        { type: "togglebox", name: "emailConfirmed", label: "Email Confirmed" },
        { type: "textbox", name: "phoneNumber", label: "Phone" },
        { type: "textbox", name: "companyPhoneNumber", label: "Company Phone" },
      ],
    },
  ],
});
